// Styles
import classnames from 'classnames'
import styled from 'styled-components/macro'

// Core
import React from 'react'

// Components, Services, Constants
import Address from 'components/Address'
import FlatList from 'components/FlatList'
import LinkIconButton from 'components/LinkIconButton'

import type {ItemProps} from 'components/FlatList/FlatList'
import {NO_DATA} from 'constants/appConstants'
import {formatData} from 'services/dataFormatter'
import {normalizeAddress} from 'services/dataMutations'

import {Card} from '@material-ui/core'
import get from 'lodash/get'
import type {ComposType, PropertyType} from 'types/propertyTypes'

type Props = {
  labels: Array<ItemProps>
  propertyData: ComposType | PropertyType
  index?: number
  isSubject?: boolean
  className?: string
}

const CompCard = ({labels, propertyData, index, isSubject, className}: Props) => {
  return (
    <CompCard.Styled className={classnames('comp-card', {subject: isSubject}, className)}>
      <FlatList items={generateListData(labels, propertyData, isSubject)} withDividers />
    </CompCard.Styled>
  )
}

function generateListData(
  labels: ItemProps[],
  propertyData: Props['propertyData'],
  isSubject?: boolean
) {
  const {displayAddressLine1, displayAddressLine4} = propertyData?.address || {}
  const initialValues: ItemProps[] = [
    {
      key: 'address',
      className: 'no-divider',
      content: (
        <div className='address-wrapper'>
          <Address
            address={normalizeAddress(displayAddressLine1, displayAddressLine4)}
            sqaPrefix='comp-card'
            twoLines
          />
          <LinkIconButton
            icon='map'
            variant='light'
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${displayAddressLine1} ${displayAddressLine4}`
            )}`}
            target='_blank'
          />
        </div>
      )
    }
  ]

  return labels.reduce((accumulator, label: ItemProps) => {
    let path: string
    let content
    switch (label.key) {
      case 'valueDate':
        path = isSubject ? 'characteristics.lastValueEffectiveDate' : 'valueEffectiveDate'
        break
      case 'valueAmount':
        path = isSubject ? 'characteristics.lastValueAmount' : label.key
        break
      case 'apn':
        path = 'address.apn'
        break
      case 'heatingCooling':
        content = formatData(label.key, [
          get(propertyData, 'characteristics.heatingName'),
          get(propertyData, 'characteristics.coolingName')
        ])
        break
      case 'bath':
        content = formatData(label.key, [
          get(propertyData, 'characteristics.bathFullCount'),
          get(propertyData, 'characteristics.bathHalfCount')
        ])
        break
      case 'pricePerSquareFoot':
        content = formatData(
          label.key,
          Math.round(
            get(propertyData, isSubject ? 'characteristics.lastValueAmount' : 'valueAmount') /
              get(propertyData, 'characteristics.buildingSize')
          )
        )
        break
      default:
        path = `characteristics.${label.key}`
    }
    if (!content) {
      content = formatData(label.key, get(propertyData, path!))
    }
    const data = {
      key: label.key,
      content: content ?? NO_DATA
    }
    accumulator.push(data)
    return accumulator
  }, initialValues)
}

CompCard.Styled = styled(Card)`
  &.comp-card {
    min-width: 268px;
    width: 268px;
    padding: 0 24px;
    border: 1px solid ${({theme}) => theme.colors.stone.light};
    background-color: ${({theme}) => theme.colors.grayscale.white};
    box-shadow: none;

    &.subject {
      background-color: ${({theme}) => theme.colors.stone.lighter};

      .list-header {
        background-color: ${({theme}) => theme.colors.stone.light};
        height: 31px;
      }

      .flat-list > li:last-child {
        height: 48px;
      }
    }

    .address-wrapper {
      margin: 24px 0 14px;
      display: flex;
      align-items: flex-start;
      max-width: 100%;
      width: 100%;

      .address {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        overflow: hidden;
        margin-right: auto;

        .street,
        .city-state-zip {
          white-space: nowrap;
          text-overflow: ellipsis;
          display: inline-block;
          overflow: hidden;
          max-width: 100%;
        }
      }

      a {
        margin-left: 8px;
        align-items: flex-start;
        justify-content: flex-end;
      }
    }

    .flat-list > li:last-child {
      height: 47px;
    }

    .comp-image {
      border-radius: 4px;
      margin-bottom: 16px;
    }

    .status-container {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;

      .chip {
        margin-right: 15px;
      }
    }

    .no-divider {
      box-shadow: none;
    }
  }
`

export default CompCard
